import { FEATURE_TAGS, PAYMENT_STATUS, RERSERVATION_STATUS } from "../../witadmin/Common/constants";

export interface ReservationProps {
  id: number;
  propertyId: string;
  bookingId: string;
  multipleBookingId: string;
  bookingDate: string;
  checkin: string;
  checkout: string;
  customer: Customer;
  rooms: number;
  totalAmount: number;
  deposit: number;
  currency: string;
  status: string;
  statusConfig: number;
  paymentType: string;
  paymentStatus: string;
  lastModificationDate?: string;
  nights: number;
  inventoryTicker: string;
  accommodationTicker: string;
  configurationTicker?: string;
  mealPlanTicker?: string;
  conditionTicker?: string;
  capacity: number;
  advance?: number;
  affiliate?: string;
  userEmailStatus?: string;
  hotelEmailStatus?: string;
  extras?: string;
  insurance?: string;
  promotions?: string;
  comments?: string;
  country?: string;
  isCombined: boolean;
  promoCode?: string;
  origin?: string;
  mode?: string;
  locale?: string;
  organizationName?: string;
  organizationType?: string;
  noShow: string;
  communicationConsent: boolean;
  internalNotes?: string;
  tierTicker?: string;
  cancellationDate?: string;
  cancellationReason?: string;
  onlineStoreOrderId?: string;
  tags?: string;
  bundleTicker?: string;
  modifierUsers?: string;
  hasAutomaticCharges?: boolean;
  lastEditor?: string;
  extendedReservationId?: string;
}

export class Reservation {
  private constructor(
    readonly id: number,
    readonly propertyId: string,
    readonly bookingId: string,
    readonly multipleBookingId: string,
    readonly bookingDate: string,
    readonly checkin: string,
    readonly checkout: string,
    readonly customer: Customer,
    readonly rooms: number,
    readonly totalAmount: number,
    readonly deposit: number,
    readonly currency: string,
    readonly status: string,
    readonly statusConfig: number,
    readonly paymentType: string,
    readonly paymentStatus: string,
    readonly nights: number,
    readonly inventoryTicker: string,
    readonly accommodationTicker: string,
    readonly capacity: number,
    readonly isCombined: boolean,
    readonly noShow: string,
    readonly communicationConsent: boolean,
    readonly configurationTicker?: string,
    readonly mealPlanTicker?: string,
    readonly conditionTicker?: string,
    readonly advance?: number,
    readonly affiliate?: string,
    readonly lastModificationDate?: string,
    readonly userEmailStatus?: string,
    readonly hotelEmailStatus?: string,
    readonly extras?: string,
    readonly insurance?: string,
    readonly promotions?: string,
    readonly comments?: string,
    readonly country?: string,
    readonly promoCode?: string,
    readonly origin?: string,
    readonly mode?: string,
    readonly locale?: string,
    readonly organizationName?: string,
    readonly organizationType?: string,
    readonly internalNotes?: string,
    readonly tierTicker?: string,
    readonly cancellationDate?: string,
    readonly cancellationReason?: string,
    readonly onlineStoreOrderId?: string,
    readonly tags?: string,
    readonly bundleTicker?: string,
    readonly modifierUsers?: string,
    readonly hasAutomaticCharges?: boolean,
    readonly lastEditor?: string,
    readonly extendedReservationId?: string
  ) {}

  get isSuccess(): boolean {
    return (
      this.status === RERSERVATION_STATUS.OK &&
      (this.statusConfig === 2 || this.statusConfig === 0) &&
      this.paymentStatus === PAYMENT_STATUS.SUCCESS &&
      !this.cancellationDate
    );
  }

  get isFailed(): boolean {
    return (
      this.status === RERSERVATION_STATUS.PEND &&
      this.statusConfig === 3 &&
      this.paymentStatus === PAYMENT_STATUS.FAILED &&
      !this.cancellationDate
    );
  }

  get isNotCompleted(): boolean {
    return (
      this.status === RERSERVATION_STATUS.PEND &&
      this.statusConfig === 1 &&
      this.paymentStatus === PAYMENT_STATUS.NOT_COMPLETED &&
      !this.cancellationDate
    );
  }

  get isTransferPending(): boolean {
    return (
      this.status === RERSERVATION_STATUS.OK &&
      this.statusConfig === 4 &&
      this.paymentStatus === PAYMENT_STATUS.PENDING &&
      !this.cancellationDate
    );
  }

  get isQuotationPending(): boolean {
    return (
      this.isQuotation() &&
      this.status === RERSERVATION_STATUS.PEND &&
      this.statusConfig === 4 &&
      this.paymentStatus === PAYMENT_STATUS.PENDING &&
      !this.cancellationDate
    );
  }

  get isExpired(): boolean {
    return (
      this.status === RERSERVATION_STATUS.OK &&
      this.statusConfig === 6 &&
      this.paymentStatus === PAYMENT_STATUS.EXPIRED &&
      !this.cancellationDate
    );
  }

  get isCancelled(): boolean {
    return this.status === RERSERVATION_STATUS.CXL && this.cancellationDate != null;
  }

  private isQuotation(): boolean {
    return Boolean(this.tags?.includes(FEATURE_TAGS.quotation));
  }

  public static of(props: ReservationProps): Reservation {
    return new Reservation(
      props.id,
      props.propertyId,
      props.bookingId,
      props.multipleBookingId,
      props.bookingDate,
      props.checkin,
      props.checkout,
      props.customer,
      props.rooms,
      props.totalAmount,
      props.deposit,
      props.currency,
      props.status,
      props.statusConfig,
      props.paymentType,
      props.paymentStatus,
      props.nights,
      props.inventoryTicker,
      props.accommodationTicker,
      props.capacity,
      props.isCombined,
      props.noShow,
      props.communicationConsent,
      props.configurationTicker,
      props.mealPlanTicker,
      props.conditionTicker,
      props.advance,
      props.affiliate,
      props.lastModificationDate,
      props.userEmailStatus,
      props.hotelEmailStatus,
      props.extras,
      props.insurance,
      props.promotions,
      props.comments,
      props.country,
      props.promoCode,
      props.origin,
      props.mode,
      props.locale,
      props.organizationName,
      props.organizationType,
      props.internalNotes,
      props.tierTicker,
      props.cancellationDate,
      props.cancellationReason,
      props.onlineStoreOrderId,
      props.tags,
      props.bundleTicker,
      props.modifierUsers,
      props.hasAutomaticCharges,
      props.lastEditor,
      props.extendedReservationId
    );
  }
}

export interface Customer {
  name: string;
  lastName: string;
  email: string;
  phone: string;
}
